import React from "react";
import { Accordion, Container } from "react-bootstrap";
import "./style.css";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  title: string;
  faqItems: FAQItem[];
}

const FAQSection: React.FC<FAQProps> = ({ title, faqItems }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <Container className="my-5" ref={ref}>
      <div className="row justify-content-center g-4">
        <div className="d-flex flex-column col-12 col-md-6">
          <h2
            className="gradient-text mb-4 text-center text-md-start"
            style={{ fontSize: "2.2rem", fontWeight: 800 }}
          >
            {title}
          </h2>
          <Accordion className="faq-accordion" defaultActiveKey={undefined}>
            {faqItems.map((faq, index) => (
              <Accordion.Item
                eventKey={index.toString()}
                key={index}
                className="faq-item"
              >
                <Accordion.Header>{faq.question}</Accordion.Header>
                <Accordion.Body>{faq.answer}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
        <div className="faq-illustration col-12 col-md-6 text-center">
          <motion.img
            src="/Buoyancy Images/Img_06.02.webp"
            alt="Illustration"
            style={{ maxWidth: "100%", height: "auto", borderRadius: "20px" }}
            initial={{ opacity: 0, y: 100 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            whileHover={{
              x: [0, 10, -10, 10, 0],
              transition: {
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          />
        </div>
      </div>
    </Container>
  );
};


const singleFaceSwapFAQs: FAQItem[] = [
  {
    question: "Is SwapToon free to use?",
    answer:
      "Yes! SwapToon offers a free version with basic features. For advanced face swap options, premium filters, and high-quality face morphing, you can upgrade to SwapToon Pro.",
  },
  {
    question: "Does SwapToon work on all devices?",
    answer:
      "Yes! SwapToon is available for both mobile and desktop users, making it accessible from anywhere.",
  },
  {
    question: "Is my data safe with SwapToon?",
    answer:
      "Absolutely! We prioritize user privacy and do not store or share your images.",
  },
  {
    question: "Can I swap faces with celebrities and cartoon characters?",
    answer:
      "Yes! Our app lets you swap faces with anyone, including celebrities, characters, and even pets.",
  },
];

const multiFaceSwapFAQs: FAQItem[] = [
  {
    question: "Can I swap more than two faces in one photo?",
    answer:
      "Yes! SwapToon allows multiple face swaps in a single image, perfect for groups, couples, and families.",
  },
  {
    question: "Is the face swap realistic?",
    answer:
      "Absolutely! SwapToon’s AI-powered technology ensures smooth blending and high-quality transformations.",
  },
  {
    question: "Does SwapToon store my images?",
    answer:
      "No! We value privacy, and all photos are processed securely without being stored.",
  },
  {
    question: "Can I swap faces with celebrities or cartoon characters?",
    answer:
      "Yes! SwapToon lets you upload any image to swap faces with your favorite stars or animated characters.",
  },
];

export const SingleFaceSwapFAQ: React.FC = () => (
  <FAQSection
    title="Frequently Asked Questions"
    faqItems={singleFaceSwapFAQs}
  />
);

export const MultiFaceSwapFAQ: React.FC = () => (
  <FAQSection title="Frequently Asked Questions" faqItems={multiFaceSwapFAQs} />
);

const preweddingFAQs: FAQItem[] = [
  {
    question: "Can both bride and groom swap their faces in one click?",
    answer:
      "Yes! SwapToon automatically detects both faces in the couple photo and allows you to upload target faces for both the bride and groom seamlessly.",
  },
  {
    question: "Is Pre-Wedding Face Swap free to try?",
    answer:
      "Yes! You can upload pre-wedding templates and swap couple faces for free with high quality.",
  },
  {
    question: "What image quality should I upload for best results?",
    answer:
      "For best pre-wedding results, upload clear, high-resolution frontal photos of both partners with good lighting.",
  },
  {
    question: "Are my uploaded couple photos kept private and safe?",
    answer:
      "Absolutely! We prioritize user privacy and encrypt all image transfers. Your uploaded images are never stored or shared.",
  },
];

export const PreweddingFAQ: React.FC = () => (
  <FAQSection title="Frequently Asked Questions" faqItems={preweddingFAQs} />
);

const babyshowerFAQs: FAQItem[] = [
  {
    question: "Can I swap faces for both parents and family members in baby shower photos?",
    answer:
      "Yes! SwapToon detects multiple faces in baby shower and group photos, allowing you to swap mom, dad, grandparents, and guests in a single click.",
  },
  {
    question: "Is Baby Shower Face Swap free to use?",
    answer:
      "Yes! You can upload baby shower templates and swap faces for free.",
  },
  {
    question: "Can I use swapped photos for baby shower digital invitations?",
    answer:
      "Definitely! Download high-resolution swapped images and use them directly for digital invitation cards, gender reveal banners, and social posts.",
  },
  {
    question: "Is my privacy guaranteed for maternity and family photos?",
    answer:
      "Yes, your privacy is our top priority. We do not store or share your uploaded family or maternity photos.",
  },
];

export const BabyshowerFAQ: React.FC = () => (
  <FAQSection title="Frequently Asked Questions" faqItems={babyshowerFAQs} />
);

export default FAQSection;
